import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../../../providers/ToastProvider';
import { copyToClipboard } from '../../../../utils/clipboard';
import type { PracticeDetail } from '../../../../services/practiceDetail';
import { findEmptyNonRequiredFields } from './detailCompleteness';
import { PracticeActivityCard, PRACTICE_ACTIVITY_ENTRIES } from '../timeline/PracticeActivityCard';
import { usePracticeTimeline } from '../../../../hooks/usePracticeTimeline';

interface RecordRow {
    label: string;
    value: string;
}

function RecordCard({ rows }: { rows: RecordRow[] }) {
    const { showToast } = useToast();
    if (rows.length === 0) return null;

    const handleCopy = async (row: RecordRow) => {
        const copied = await copyToClipboard(row.value);
        showToast(copied ? `${row.label} copied` : 'Could not copy', copied ? 'success' : 'error');
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)] px-4 py-3.5 flex flex-col gap-3">
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.06em]">
                Record
            </span>
            <div className="flex flex-col gap-2.5">
                {rows.map((row) => (
                    <div key={row.label} className="flex items-center gap-2 min-w-0">
                        <span className="text-[12px] text-gray-500 flex-1 truncate">
                            {row.label}
                        </span>
                        <button
                            type="button"
                            onClick={() => void handleCopy(row)}
                            className="group flex items-center gap-1.5 shrink-0"
                        >
                            <span className="font-mono text-[12px] text-gray-800">{row.value}</span>
                            <ClipboardDocumentIcon className="h-3 w-3 text-gray-300 group-hover:text-gray-700" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

interface NeedsAttentionCardProps {
    fields: { key: string; label: string }[];
    onJumpToField: (fieldKey: string) => void;
}

function NeedsAttentionCard({ fields, onJumpToField }: NeedsAttentionCardProps) {
    if (fields.length === 0) return null;

    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)] px-4 py-3.5 flex flex-col gap-3">
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.06em]">
                Needs attention
            </span>
            <div className="flex flex-col gap-2">
                {fields.map((field) => (
                    <button
                        key={field.key}
                        type="button"
                        onClick={() => onJumpToField(field.key)}
                        className="flex items-center gap-2 text-left group"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FECA42] shrink-0" />
                        <span className="text-[12px] font-medium text-gray-700 group-hover:text-gray-900 flex-1 truncate">
                            {field.label}
                        </span>
                        <span className="text-[11px] text-gray-400 shrink-0">empty</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

const MAX_NEEDS_ATTENTION_FIELDS = 6;

interface DetailAsideProps {
    practice: PracticeDetail;
    values: Record<string, string>;
    onJumpToField: (fieldKey: string) => void;
}

// Right rail for the Details tab (see D2Aside in the design source) - hidden
// below the xl breakpoint, same as the mock's "dead space becomes lookup
// facts" framing. All three of the mock's cards are now built:
//   - "Record": copyable ids from the SAVED practice (not live edits).
//   - "Needs attention": empty, non-required fields across Info+Account.
//   - "Recent activity": the mock's "Recent edits" slot, unblocked now that
//     GET /practices/:id/timeline exists. Widened past edits to the merged
//     feed - practice changes and logged calls tell one story about the
//     account - and it collapses itself when there is nothing to show.
export function DetailAside({ practice, values, onJumpToField }: DetailAsideProps) {
    const recordRows: RecordRow[] = [
        { label: 'DM practice ID', value: practice.account?.dmPracticeId ?? '' },
        { label: 'DM doctor ID', value: practice.account?.dmDoctorId ?? '' },
        { label: 'Bill.com ID', value: practice.account?.billId ?? '' }
    ].filter((row) => row.value.trim() !== '');

    const emptyFields = findEmptyNonRequiredFields(values, MAX_NEEDS_ATTENTION_FIELDS);

    // Fetched here rather than inside the card so this can tell whether the
    // aside has anything at all to show. A practice with no ids, no empty
    // fields and no history would otherwise reserve a permanently blank
    // 268px gutter. Errors count as nothing to show: the aside is supporting
    // context, and an error box beside a form the user came here to fill in
    // is worse than its absence.
    const { entries } = usePracticeTimeline(practice.id, 1, PRACTICE_ACTIVITY_ENTRIES);

    if (recordRows.length === 0 && emptyFields.length === 0 && entries.length === 0) return null;

    return (
        <aside className="hidden xl:flex xl:flex-col w-[268px] shrink-0 gap-3.5 min-h-0 overflow-y-auto scrollbar-hide">
            <RecordCard rows={recordRows} />
            <NeedsAttentionCard fields={emptyFields} onJumpToField={onJumpToField} />
            <PracticeActivityCard entries={entries} />
        </aside>
    );
}
